import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import spotifyService, {
  SpotifyConnectionStatus,
  SpotifyTrack,
  EventPlaylist,
} from '@/services/spotify.service';
import type { RootState } from './index';

// ============================================
// TYPES
// ============================================

interface SpotifyState {
  // Connection status
  connection: SpotifyConnectionStatus | null;

  // Current event playlist
  currentPlaylist: EventPlaylist | null;

  // Search results
  searchResults: SpotifyTrack[];
  searchTotal: number;
  searchQuery: string;

  // Loading states
  loading: {
    isCheckingConnection: boolean;
    isConnecting: boolean;
    isDisconnecting: boolean;
    isLoadingPlaylist: boolean;
    isCreatingPlaylist: boolean;
    isSearching: boolean;
    isAddingTracks: boolean;
    isRemovingTracks: boolean;
    isSyncing: boolean;
  };

  // Error state
  error: string | null;
}

// ============================================
// INITIAL STATE
// ============================================

const initialState: SpotifyState = {
  connection: null,
  currentPlaylist: null,
  searchResults: [],
  searchTotal: 0,
  searchQuery: '',
  loading: {
    isCheckingConnection: false,
    isConnecting: false,
    isDisconnecting: false,
    isLoadingPlaylist: false,
    isCreatingPlaylist: false,
    isSearching: false,
    isAddingTracks: false,
    isRemovingTracks: false,
    isSyncing: false,
  },
  error: null,
};

// ============================================
// ASYNC THUNKS
// ============================================

/**
 * Check Spotify connection status
 */
export const checkConnectionStatus = createAsyncThunk(
  'spotify/checkConnectionStatus',
  async (_, { rejectWithValue }) => {
    try {
      return await spotifyService.getConnectionStatus();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * Get Spotify auth URL
 */
export const getAuthUrl = createAsyncThunk(
  'spotify/getAuthUrl',
  async (redirectUrl: string | undefined, { rejectWithValue }) => {
    try {
      return await spotifyService.getAuthUrl(redirectUrl);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * Disconnect Spotify account
 */
export const disconnectSpotify = createAsyncThunk(
  'spotify/disconnect',
  async (_, { rejectWithValue }) => {
    try {
      await spotifyService.disconnect();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * Get event playlist
 */
export const fetchEventPlaylist = createAsyncThunk(
  'spotify/fetchEventPlaylist',
  async (eventId: string, { rejectWithValue }) => {
    try {
      return await spotifyService.getEventPlaylist(eventId);
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * Create event playlist
 */
export const createEventPlaylist = createAsyncThunk(
  'spotify/createEventPlaylist',
  async (
    { eventId, name, description }: { eventId: string; name: string; description?: string },
    { rejectWithValue }
  ) => {
    try {
      return await spotifyService.createPlaylist(eventId, { name, description });
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * Search for tracks
 */
export const searchTracks = createAsyncThunk(
  'spotify/searchTracks',
  async (
    { query, limit, offset }: { query: string; limit?: number; offset?: number },
    { rejectWithValue }
  ) => {
    try {
      const result = await spotifyService.searchTracks(query, limit, offset);
      return { ...result, query };
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * Add tracks to playlist
 */
export const addTracksToPlaylist = createAsyncThunk(
  'spotify/addTracksToPlaylist',
  async (
    { eventId, trackUris }: { eventId: string; trackUris: string[] },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const result = await spotifyService.addTracks(eventId, trackUris);
      // Refresh playlist after adding
      dispatch(fetchEventPlaylist(eventId));
      return result;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * Remove tracks from playlist
 */
export const removeTracksFromPlaylist = createAsyncThunk(
  'spotify/removeTracksFromPlaylist',
  async (
    { eventId, trackUris }: { eventId: string; trackUris: string[] },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const result = await spotifyService.removeTracks(eventId, trackUris);
      // Refresh playlist after removing
      dispatch(fetchEventPlaylist(eventId));
      return result;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

/**
 * Sync playlist with Spotify
 */
export const syncPlaylist = createAsyncThunk(
  'spotify/syncPlaylist',
  async (eventId: string, { rejectWithValue, dispatch }) => {
    try {
      await spotifyService.syncPlaylist(eventId);
      // Refresh playlist after sync
      dispatch(fetchEventPlaylist(eventId));
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

// ============================================
// SLICE
// ============================================

const spotifySlice = createSlice({
  name: 'spotify',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
      state.searchTotal = 0;
      state.searchQuery = '';
    },
    clearCurrentPlaylist: (state) => {
      state.currentPlaylist = null;
    },
    setConnectionStatus: (state, action: PayloadAction<SpotifyConnectionStatus>) => {
      state.connection = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Check connection status
      .addCase(checkConnectionStatus.pending, (state) => {
        state.loading.isCheckingConnection = true;
        state.error = null;
      })
      .addCase(checkConnectionStatus.fulfilled, (state, action) => {
        state.loading.isCheckingConnection = false;
        state.connection = action.payload;
      })
      .addCase(checkConnectionStatus.rejected, (state, action) => {
        state.loading.isCheckingConnection = false;
        state.error = action.payload as string;
      })

      // Get auth URL
      .addCase(getAuthUrl.pending, (state) => {
        state.loading.isConnecting = true;
        state.error = null;
      })
      .addCase(getAuthUrl.fulfilled, (state) => {
        state.loading.isConnecting = false;
      })
      .addCase(getAuthUrl.rejected, (state, action) => {
        state.loading.isConnecting = false;
        state.error = action.payload as string;
      })

      // Disconnect
      .addCase(disconnectSpotify.pending, (state) => {
        state.loading.isDisconnecting = true;
        state.error = null;
      })
      .addCase(disconnectSpotify.fulfilled, (state) => {
        state.loading.isDisconnecting = false;
        state.connection = { connected: false };
      })
      .addCase(disconnectSpotify.rejected, (state, action) => {
        state.loading.isDisconnecting = false;
        state.error = action.payload as string;
      })

      // Fetch event playlist
      .addCase(fetchEventPlaylist.pending, (state) => {
        state.loading.isLoadingPlaylist = true;
        state.error = null;
      })
      .addCase(fetchEventPlaylist.fulfilled, (state, action) => {
        state.loading.isLoadingPlaylist = false;
        state.currentPlaylist = action.payload;
      })
      .addCase(fetchEventPlaylist.rejected, (state, action) => {
        state.loading.isLoadingPlaylist = false;
        state.error = action.payload as string;
      })

      // Create event playlist
      .addCase(createEventPlaylist.pending, (state) => {
        state.loading.isCreatingPlaylist = true;
        state.error = null;
      })
      .addCase(createEventPlaylist.fulfilled, (state, action) => {
        state.loading.isCreatingPlaylist = false;
        state.currentPlaylist = action.payload;
      })
      .addCase(createEventPlaylist.rejected, (state, action) => {
        state.loading.isCreatingPlaylist = false;
        state.error = action.payload as string;
      })

      // Search tracks
      .addCase(searchTracks.pending, (state) => {
        state.loading.isSearching = true;
        state.error = null;
      })
      .addCase(searchTracks.fulfilled, (state, action) => {
        state.loading.isSearching = false;
        state.searchResults = action.payload.tracks;
        state.searchTotal = action.payload.total;
        state.searchQuery = action.payload.query;
      })
      .addCase(searchTracks.rejected, (state, action) => {
        state.loading.isSearching = false;
        state.error = action.payload as string;
      })

      // Add tracks
      .addCase(addTracksToPlaylist.pending, (state) => {
        state.loading.isAddingTracks = true;
        state.error = null;
      })
      .addCase(addTracksToPlaylist.fulfilled, (state) => {
        state.loading.isAddingTracks = false;
      })
      .addCase(addTracksToPlaylist.rejected, (state, action) => {
        state.loading.isAddingTracks = false;
        state.error = action.payload as string;
      })

      // Remove tracks
      .addCase(removeTracksFromPlaylist.pending, (state) => {
        state.loading.isRemovingTracks = true;
        state.error = null;
      })
      .addCase(removeTracksFromPlaylist.fulfilled, (state) => {
        state.loading.isRemovingTracks = false;
      })
      .addCase(removeTracksFromPlaylist.rejected, (state, action) => {
        state.loading.isRemovingTracks = false;
        state.error = action.payload as string;
      })

      // Sync playlist
      .addCase(syncPlaylist.pending, (state) => {
        state.loading.isSyncing = true;
        state.error = null;
      })
      .addCase(syncPlaylist.fulfilled, (state) => {
        state.loading.isSyncing = false;
      })
      .addCase(syncPlaylist.rejected, (state, action) => {
        state.loading.isSyncing = false;
        state.error = action.payload as string;
      });
  },
});

// ============================================
// ACTIONS & SELECTORS
// ============================================

export const { clearError, clearSearchResults, clearCurrentPlaylist, setConnectionStatus } =
  spotifySlice.actions;

// Selectors
export const selectSpotifyConnection = (state: RootState) => state.spotify.connection;
export const selectCurrentPlaylist = (state: RootState) => state.spotify.currentPlaylist;
export const selectSearchResults = (state: RootState) => state.spotify.searchResults;
export const selectSearchTotal = (state: RootState) => state.spotify.searchTotal;
export const selectSearchQuery = (state: RootState) => state.spotify.searchQuery;
export const selectSpotifyLoading = (state: RootState) => state.spotify.loading;
export const selectSpotifyError = (state: RootState) => state.spotify.error;
export const selectIsSpotifyConnected = (state: RootState) =>
  state.spotify.connection?.connected ?? false;

export default spotifySlice.reducer;
